import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const FPS = {};
let fpsLogged = false;
function logFps() {
  if (fpsLogged) return;
  fpsLogged = true;
  setTimeout(() => {
    const rows = Object.entries(FPS).map(([id, n]) => `${id}: ${(n / 10).toFixed(1)} fps`);
    console.log(`[AV live] frame updates per camera (10s): ${rows.join(" · ")}`);
  }, 10000);
}

const visible = () => (document.hidden ? new Promise((r) => document.addEventListener("visibilitychange", r, { once: true })) : null);

// Long-polls for all visible cameras (stays under the ingress rate limit); next request starts the moment the last returns.
function useLive(ids, w, paused) {
  const [srcs, setSrcs] = useState({});
  const key = ids.join(",");
  useEffect(() => {
    if (!key || paused) return undefined;
    let alive = true;
    const last = {};
    const list = key.split(",");
    list.forEach((id) => { FPS[id] = FPS[id] || 0; });
    logFps();
    const loop = async (delay) => {
      await new Promise((res) => setTimeout(res, delay));
      while (alive) {
        await visible();
        try {
          const r = await api.get("/avstone/live/frames", { params: { ids: key, w, after: list.map((id) => last[id] || "").join(",") } });
          r.data.frames.forEach((f) => {
            if (last[f.camera_id] && f.ts <= last[f.camera_id]) return;   // the other poller already showed it
            last[f.camera_id] = f.ts;
            const url = `data:image/jpeg;base64,${f.b64}`;
            const im = new Image();
            im.onload = () => { if (alive) { setSrcs((m) => ({ ...m, [f.camera_id]: url })); FPS[f.camera_id] += 1; } };
            im.src = url;
          });
        } catch (e) {
          await new Promise((res) => setTimeout(res, 2000));
        }
      }
    };
    loop(0); loop(120);   // two staggered long-polls so network round-trips overlap
    return () => { alive = false; };
  }, [key, w, paused]);
  return srcs;
}

const Waiting = ({ text }) => <div className="w-full h-full flex items-center justify-center text-[11px] font-mono-data text-[#71717A]">{text}</div>;

function Tile({ cam, onOpen, src }) {
  const up = cam.health === "UP" || cam.health === "DOWN";
  return (
    <button data-testid={`avlive-tile-${cam.id}`} onClick={() => onOpen(cam)} className="relative bg-black overflow-hidden min-h-0 min-w-0">
      {!up ? <Waiting text={`${cam.health} — waiting for edge agent`} />
        : src ? <img src={src} alt={cam.name} className="w-full h-full object-contain" data-testid={`avlive-img-${cam.id}`} /> : <Waiting text="connecting…" />}
      <span className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/60 text-[10px] font-mono-data text-white" data-testid={`avlive-health-${cam.id}`}>
        <span style={{ color: cam.health === "UP" ? "#30D158" : "#FF9F0A" }}>●</span> {cam.name}
      </span>
    </button>
  );
}

export default function AvStoneLive() {
  const [cams, setCams] = useState([]);
  const [open, setOpen] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    const load = () => api.get("/avstone/cameras").then((r) => setCams(r.data.filter((c) => c.rtsp_url))).catch((e) => { if ([401, 403].includes(e?.response?.status)) setErr(e.response.data?.detail || "Access denied"); });
    load();
    const t = setInterval(load, 15000);
    const h = document.documentElement, b = document.body;
    h.style.overflow = "hidden"; b.style.overflow = "hidden";
    return () => { clearInterval(t); h.style.overflow = ""; b.style.overflow = ""; };
  }, []);
  const cols = Math.max(1, Math.ceil(Math.sqrt(cams.length)));
  const rows = Math.max(1, Math.ceil(cams.length / cols));
  const tileW = Math.min(1920, Math.max(960, Math.ceil((window.innerWidth * (window.devicePixelRatio || 1) * 1.25) / cols / 320) * 320));
  const grid = useLive(cams.filter((c) => c.health === "UP" || c.health === "DOWN").map((c) => c.id), tileW, !!open);
  const big = useLive(open ? [open.id] : [], 3840, false);
  if (err) return <div className="p-6 text-[#FF3B30] font-mono-data text-sm" data-testid="avlive-error">{err}</div>;
  return (
    <div className="fixed inset-0 z-50 bg-black" style={{ width: "100vw", height: "100dvh" }} data-testid="avstone-live">
      <div className="grid w-full h-full gap-1" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))`, gridTemplateRows: `repeat(${rows}, minmax(0,1fr))` }} data-testid="avlive-grid">
        {cams.map((c) => <Tile key={c.id} cam={c} onOpen={setOpen} src={grid[c.id]} />)}
      </div>
      <div className="absolute top-0 right-0 h-7 px-2 flex items-center gap-3 bg-black/50 text-[10px] font-mono-data" data-testid="avlive-overlay">
        <span className="text-[#30D158]">● LIVE</span>
        <Link to="/avstone/dashboard" className="text-[#71717A] hover:text-white" data-testid="avlive-exit">exit</Link>
      </div>
      {open && (
        <div data-testid="avlive-modal" className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center" onClick={() => setOpen(null)}>
          <div className="absolute top-1 left-2 text-white font-mono-data text-xs">{open.name} — tap to close</div>
          {big[open.id] || grid[open.id] ? <img src={big[open.id] || grid[open.id]} alt={open.name} className="max-h-full max-w-full object-contain" data-testid="avlive-modal-img" /> : <Waiting text="connecting…" />}
        </div>
      )}
    </div>
  );
}
